/**
 * OPS interior — the room behind the OPS building (plot P2, domain 'ops').
 *
 * Pure layout + status logic, no PlayCanvas dependency (so it is unit-testable
 * without a graphics device). The visible room, work objects, DOM cards and
 * [E] handling live in opsSystem.ts, which consumes these.
 *
 * Walking in from the south entry the player faces north across the floor and
 * reads three things at once:
 *   - the QUEUE LANE (west): one abstract work ticket per waiting queue entry,
 *     in position order, head of line at the dispatch desk;
 *   - the DAEMON OBELISK (centre-north) on a dais, pulsing by health status;
 *   - the POOL BOARD (north-east wall): a row of poolSize pegs, activeAgents lit;
 *   - the INCIDENT room (north-east): one low approval kiosk per pending
 *     interaction; [E] opens the existing InteractionDialog to answer.
 *
 * Coordinates are room-LOCAL (origin-centred), exactly like the office and board
 * interiors; the island offset is applied by the opsRoot entity transform.
 */
import {
  WALL_THICKNESS, ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HEIGHT,
  type WallSegment,
} from './layout'
import type { QueueEntry, Interaction } from '../../api/types'
import type { AABB } from '../colliders'
import { daemonColor, daemonPulseSpeed } from './statusViewModels'
import { isWaitingEntry } from './queueSemantics'

// ── Room shell ──────────────────────────────────────────────────────────────

export const OPS_HALF_X = 11
export const OPS_HALF_Z = 10
export const OPS_WALL_HEIGHT = 3.6 // taller than the shared 3 m datum — an ops hall

/** Inner faces of the exterior walls (usable floor area). */
export const OPS_INNER_HALF_X = OPS_HALF_X - WALL_THICKNESS / 2
export const OPS_INNER_HALF_Z = OPS_HALF_Z - WALL_THICKNESS / 2

export const OPS_INCIDENT_ROOM = {
  centerX: 6.7,
  centerZ: -4.8,
  width: 6.4,
  depth: 4.8,
} as const

// ── Queue lane ──────────────────────────────────────────────────────────────
// A single ordered file of non-human work tickets down the west side of the
// room. The head sits at the dispatch desk at the north end; the tail runs toward the
// south entry. When more entries wait than lane slots, the surplus is collapsed
// to a "+N more" floor sign at the tail.

/** X of the work-object lane, immediately east of the licensed dispatch bank. */
export const OPS_LANE_X = -5.2
/** Z of the head work ticket — just south of the desk, facing it (north). */
export const OPS_LANE_HEAD_Z = -5.6
/** Southward spacing between work tickets in the lane. */
export const OPS_LANE_PITCH = 1.7
/** Work tickets shown before the line overflows to a sign. */
export const OPS_QUEUE_MAX = 6

/** A queue entry placed in the lane (room-local coords). */
export interface QueueSlot {
  id: string
  taskId: string
  title: string
  status: QueueEntry['status']
  /** 0 = head of line (at the desk). */
  slot: number
  x: number
  z: number
}

export interface QueueLaneLayout {
  slots: QueueSlot[]
  /** Entries beyond OPS_QUEUE_MAX, shown as "+N more". */
  overflow: number
}

/** Local Z of a lane slot (0 = head, increasing southward). */
export function opsLaneZ(slot: number): number {
  return OPS_LANE_HEAD_Z + slot * OPS_LANE_PITCH
}

/**
 * Order the queue into the lane: sorted by position ascending (lowest position
 * = front of the line = at the desk), capped at OPS_QUEUE_MAX. Pure — the ops
 * system rebuilds figures from this whenever the queue changes. Ties on
 * position fall back to submittedAt then id so the order is fully deterministic.
 */
export function layoutQueueLane(queue: readonly QueueEntry[]): QueueLaneLayout {
  const ordered = queue.filter(isWaitingEntry).sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position
    if (a.submittedAt !== b.submittedAt) return a.submittedAt < b.submittedAt ? -1 : 1
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
  const slots: QueueSlot[] = ordered.slice(0, OPS_QUEUE_MAX).map((e, slot) => ({
    id: e.id,
    taskId: e.taskId,
    title: e.title,
    status: e.status,
    slot,
    x: OPS_LANE_X,
    z: opsLaneZ(slot),
  }))
  return { slots, overflow: Math.max(0, ordered.length - OPS_QUEUE_MAX) }
}

/** Local position of the "+N more" floor sign, at the tail of the lane. */
export function opsOverflowSignPosition(): [number, number, number] {
  return [OPS_LANE_X, 0.02, opsLaneZ(OPS_QUEUE_MAX) + 0.4]
}

// ── Pool board ──────────────────────────────────────────────────────────────
// A row of pegs on the north-east wall — one per agent slot in the daemon pool.
// The first `activeAgents` are lit; the rest are dark, so the row reads as
// "N of M slots busy" at a glance.

export const OPS_POOL_MAX = 12
export const OPS_POOL_Z =
  OPS_INCIDENT_ROOM.centerZ - OPS_INCIDENT_ROOM.depth / 2 + 0.12
export const OPS_POOL_Y = 1.55
export const OPS_POOL_MIN_X = OPS_INCIDENT_ROOM.centerX - 2.6
export const OPS_POOL_MAX_X = OPS_INCIDENT_ROOM.centerX + 2.6

export interface PoolPeg {
  index: number
  x: number
  y: number
  z: number
  lit: boolean
}

/**
 * Lay out the pool board: `poolSize` pegs (capped at OPS_POOL_MAX), spread
 * evenly across the east half of the north wall, the first `activeAgents` lit.
 * Pure — negative / oversized inputs are clamped so a bad health payload can
 * never throw or draw off the wall.
 */
export function layoutPoolPegs(poolSize: number, activeAgents: number): PoolPeg[] {
  const n = Math.max(0, Math.min(OPS_POOL_MAX, Math.floor(poolSize)))
  const lit = Math.max(0, Math.min(n, Math.floor(activeAgents)))
  const span = OPS_POOL_MAX_X - OPS_POOL_MIN_X
  const pegs: PoolPeg[] = []
  for (let i = 0; i < n; i++) {
    const x = n === 1 ? (OPS_POOL_MIN_X + OPS_POOL_MAX_X) / 2 : OPS_POOL_MIN_X + (span * i) / (n - 1)
    pegs.push({ index: i, x, y: OPS_POOL_Y, z: OPS_POOL_Z, lit: i < lit })
  }
  return pegs
}

// ── Daemon obelisk ────────────────────────────────────────────────────────────

/** Centre of the daemon dais + obelisk (room-local X,Z). */
export const OPS_OBELISK_POS: [number, number] = [0, -5]
export const OPS_DAIS_RADIUS = 1.6
export const OPS_DAIS_COLLIDER: AABB = {
  minX: OPS_OBELISK_POS[0] - OPS_DAIS_RADIUS,
  maxX: OPS_OBELISK_POS[0] + OPS_DAIS_RADIUS,
  minY: 0,
  maxY: 0.3,
  minZ: OPS_OBELISK_POS[1] - OPS_DAIS_RADIUS,
  maxZ: OPS_OBELISK_POS[1] + OPS_DAIS_RADIUS,
}
export const OPS_PILLAR_COLLIDER: AABB = {
  minX: OPS_OBELISK_POS[0] - 0.3,
  maxX: OPS_OBELISK_POS[0] + 0.3,
  minY: 0.3,
  maxY: 2.9,
  minZ: OPS_OBELISK_POS[1] - 0.3,
  maxZ: OPS_OBELISK_POS[1] + 0.3,
}

/** Health status → obelisk colour ([r,g,b] 0-1). Mirrors the server room. */
export function opsHealthColor(status?: string): [number, number, number] {
  return [...daemonColor(status)]
}

/** Pulse speed for the orb by health status (down blinks fast, healthy slow). */
export function opsPulseSpeed(status?: string): number {
  return daemonPulseSpeed(status)
}

// ── Approvals area ────────────────────────────────────────────────────────────
// One non-human approval kiosk per pending interaction, distributed around the
// incident-room perimeter. [E] on the nearest opens InteractionDialog.

export const OPS_APPROVAL_MAX = 5
export const OPS_INCIDENT_DOOR_APPROACH: AABB = {
  minX: OPS_INCIDENT_ROOM.centerX - 0.6,
  maxX: OPS_INCIDENT_ROOM.centerX + 0.6,
  minY: 0,
  maxY: OPS_WALL_HEIGHT,
  minZ: OPS_INCIDENT_ROOM.centerZ + OPS_INCIDENT_ROOM.depth / 2 - 0.1,
  maxZ: OPS_INCIDENT_ROOM.centerZ + OPS_INCIDENT_ROOM.depth / 2 + 2,
}
export const OPS_QUEUE_ENVELOPE: AABB = {
  minX: OPS_LANE_X - 0.6,
  maxX: OPS_LANE_X + 0.6,
  minY: 0,
  maxY: 2.2,
  minZ: OPS_LANE_HEAD_Z - 0.6,
  maxZ: opsLaneZ(OPS_QUEUE_MAX - 1) + 0.6,
}
export const OPS_APPROVAL_X0 = 4.1
export const OPS_APPROVAL_Z = -6.2
export const OPS_APPROVAL_PITCH = 2.6
/** Approval kiosks face the room centre (north-west) so the player sees them. */
export const OPS_APPROVAL_YAW = 200

export interface ApprovalSlot {
  interactionId: string
  runId: string
  slot: number
  x: number
  z: number
}

export interface ApprovalLayout {
  slots: ApprovalSlot[]
  overflow: number
}

/** Local position of an approval kiosk by slot. */
export function opsApprovalPosition(slot: number): [number, number, number] {
  // Three kiosks line the incident room's north wall; two occupy the front
  // corners. The central door and round-table conversation space remain clear.
  if (slot < 3) {
    return [OPS_APPROVAL_X0 + slot * OPS_APPROVAL_PITCH, 0, OPS_APPROVAL_Z]
  }
  return [slot === 3 ? OPS_APPROVAL_X0 : OPS_APPROVAL_X0 + OPS_APPROVAL_PITCH * 2, 0, -3.5]
}

/**
 * Every pending interaction, in stable id order, capped at OPS_APPROVAL_MAX.
 * Already-responded interactions are dropped (a kiosk exists only while a
 * human answer is genuinely outstanding). Pure.
 */
export function layoutApprovals(interactions: readonly Interaction[]): ApprovalLayout {
  const pending = interactions
    .filter((i) => !i.respondedAt)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const slots: ApprovalSlot[] = pending.slice(0, OPS_APPROVAL_MAX).map((i, slot) => {
    const [x, , z] = opsApprovalPosition(slot)
    return { interactionId: i.id, runId: i.runId, slot, x, z }
  })
  return { slots, overflow: Math.max(0, pending.length - OPS_APPROVAL_MAX) }
}

/** Flatten the store's per-run interaction map to a single pending list. */
export function pendingInteractions(byRun: Record<string, Interaction[]>): Interaction[] {
  const out: Interaction[] = []
  for (const list of Object.values(byRun)) {
    for (const i of list) if (!i.respondedAt) out.push(i)
  }
  return out
}

// ── Room walls ────────────────────────────────────────────────────────────────

function wallX(id: string, x: number, minZ: number, maxZ: number): WallSegment {
  return {
    id, minX: x - WALL_THICKNESS / 2, maxX: x + WALL_THICKNESS / 2,
    minZ, maxZ, minY: 0, maxY: OPS_WALL_HEIGHT, material: 'cream',
  }
}

function wallZ(id: string, z: number, minX: number, maxX: number): WallSegment {
  return {
    id, minX, maxX, minZ: z - WALL_THICKNESS / 2, maxZ: z + WALL_THICKNESS / 2,
    minY: 0, maxY: OPS_WALL_HEIGHT, material: 'cream',
  }
}

/**
 * Every wall of the ops room: a solid shell with a south entry gap
 * (x ∈ ±ENTRY_DOOR_HALF_WIDTH) closed above ENTRY_DOOR_HEIGHT by a lintel.
 * Door gaps are expressed as missing segments, so colliders inherit them.
 */
export function computeOpsWalls(): WallSegment[] {
  return [
    wallZ('ops-wall-north', -OPS_HALF_Z, -OPS_HALF_X, OPS_HALF_X),
    wallZ('ops-wall-south-west', OPS_HALF_Z, -OPS_HALF_X, -ENTRY_DOOR_HALF_WIDTH),
    wallZ('ops-wall-south-east', OPS_HALF_Z, ENTRY_DOOR_HALF_WIDTH, OPS_HALF_X),
    // Lintel closes the wall above the door opening.
    {
      ...wallZ('ops-entry-lintel', OPS_HALF_Z, -ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HALF_WIDTH),
      minY: ENTRY_DOOR_HEIGHT,
    },
    wallX('ops-wall-west', -OPS_HALF_X, -OPS_HALF_Z, OPS_HALF_Z),
    wallX('ops-wall-east', OPS_HALF_X, -OPS_HALF_Z, OPS_HALF_Z),
  ]
}
